import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import UnsavedChangesDialog from '../components/automations/UnsavedChangesDialog';

const ActionEditorUnsavedContext = createContext(null);

export function ActionEditorUnsavedProvider({ children }) {
  const [dirtyEditors, setDirtyEditors] = useState(() => new Map());
  const [showDialog, setShowDialog] = useState(false);
  const pendingActionRef = useRef(null);

  const setEditorDirty = useCallback((editorId, isDirty) => {
    if (!editorId) return;
    setDirtyEditors((prev) => {
      const next = new Map(prev);
      if (isDirty) next.set(editorId, true);
      else next.delete(editorId);
      return next;
    });
  }, []);

  const isAnyEditorDirty = dirtyEditors.size > 0;

  const guardAction = useCallback((action) => {
    if (!isAnyEditorDirty) {
      action?.();
      return;
    }
    pendingActionRef.current = action;
    setShowDialog(true);
  }, [isAnyEditorDirty]);

  const confirmDiscard = useCallback(() => {
    setDirtyEditors(new Map());
    setShowDialog(false);
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    action?.();
  }, []);

  const cancelDiscard = useCallback(() => {
    pendingActionRef.current = null;
    setShowDialog(false);
  }, []);

  useEffect(() => {
    if (!isAnyEditorDirty) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = 'You have unsaved changes.';
      return event.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isAnyEditorDirty]);

  const value = useMemo(
    () => ({
      setEditorDirty,
      guardAction,
      isAnyEditorDirty,
      confirmDiscard,
      cancelDiscard,
    }),
    [setEditorDirty, guardAction, isAnyEditorDirty, confirmDiscard, cancelDiscard],
  );

  return (
    <ActionEditorUnsavedContext.Provider value={value}>
      {children}
      <UnsavedChangesDialog
        open={showDialog}
        onCancel={cancelDiscard}
        onConfirm={confirmDiscard}
      />
    </ActionEditorUnsavedContext.Provider>
  );
}

export function useActionEditorUnsaved() {
  const context = useContext(ActionEditorUnsavedContext);
  if (!context) {
    throw new Error('useActionEditorUnsaved must be used within ActionEditorUnsavedProvider');
  }
  return context;
}

export function useOptionalActionEditorUnsaved() {
  return useContext(ActionEditorUnsavedContext);
}
